import { useCallback, useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';

import {
  DefaultDescriptionBenefitEducation,
  FormBase,
  FormModal,
  Logger,
  UploadImageModal,
  formatUnknownErrorMessage,
  useToast,
} from '@gbs-monorepo-packages/common';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  type UsersUpdateSchema,
  userUpdateSchema,
} from '../../../../formSchemas/userSchema';
import { deleteMemberProfile } from '../../../../services/users';

interface IUpdateTeammatesProps {
  onAccept: (data: UsersUpdateSchema) => Promise<boolean>;
  onDecline: () => void;
  onProfileChange: (value: string, userId: number) => void;
  open: boolean;
  loading: boolean;
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  currentRole: string;
  profileSrc: string;
}

export const ModalUpdateUser = ({
  onAccept,
  onDecline,
  onProfileChange,
  open,
  loading = false,
  id,
  email,
  firstName,
  lastName,
  phone,
  currentRole,
  profileSrc,
}: IUpdateTeammatesProps): JSX.Element => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [loadingDeleteImage, setLoadingDeleteImage] = useState<boolean>(false);
  const { addToast } = useToast();

  const updateUser = useForm<UsersUpdateSchema>({
    resolver: zodResolver(userUpdateSchema),
  });

  const {
    formState: { errors },
    setValue,
    watch,
    handleSubmit,
  } = updateUser;

  const values = watch();

  const resetForm = useCallback(() => {
    setValue('email', email);
    setValue('firstName', firstName);
    setValue('lastName', lastName);
    setValue('phone', phone);
    setValue('currentRole', currentRole);
    setImageFile(null);
  }, [email, firstName, lastName, phone, currentRole, setValue]);

  const handleDeclineAddTeammates = useCallback(() => {
    resetForm();
    onDecline?.();
  }, [onDecline, resetForm]);

  useEffect(() => {
    if (!loading) {
      resetForm();
    }
  }, [loading]);

  const handleDeleteImage = useCallback(
    (id: number) => {
      setLoadingDeleteImage(true);

      deleteMemberProfile(id)
        .then(() => {
          setLoadingDeleteImage(false);
          setImageFile(null);
          onProfileChange('', id);
          addToast({
            title: 'Image deleted',
            description: `Profile image successfully deleted.`,
            styleType: 'success',
            dataCy: 'success-toast',
            duration: 3000,
          });
        })
        .catch((err) => {
          setLoadingDeleteImage(false);
          addToast({
            title: 'Error on delete image',
            description: formatUnknownErrorMessage(
              err,
              DefaultDescriptionBenefitEducation
            ),
            styleType: 'error',
            dataCy: 'error-toast',
            duration: 3000,
          });
          Logger.debug('err on delete image: ', err);
        });
    },
    [addToast]
  );

  useEffect(() => {
    resetForm();
  }, [resetForm]);

  const onSubmit: SubmitHandler<UsersUpdateSchema> = async (
    data: UsersUpdateSchema
  ) => {
    const userAux = {
      ...data,
    };

    if (imageFile) {
      userAux.profile = imageFile;
    }

    await onAccept?.(userAux).then(() => {
      resetForm();
    });
  };

  return !open ? (
    <></>
  ) : (
    <FormBase.Provider {...updateUser}>
      <FormModal
        biggerModal
        acceptText="Update User"
        declineText="Cancel"
        open={open}
        dataCy="title-editUser"
        mainText="Edit User"
        onOpenChange={handleDeclineAddTeammates}
        onDecline={handleDeclineAddTeammates}
        onAccept={handleSubmit(onSubmit)}
        loading={loading || loadingDeleteImage}
        formId="add-clientForm"
        zIndex={10}
      >
        <FormBase.Content>
          <UploadImageModal
            label="User Image"
            imageSrc={profileSrc}
            onSelectImage={(file) => {
              setImageFile(file);
            }}
            onRemoveImagePreview={() => {
              setImageFile(null);
            }}
            onRemoveImage={() => {
              handleDeleteImage(id);
            }}
            loadingDeleteImage={loadingDeleteImage || loading}
            loading={loading}
          />
          <FormBase.Grid>
            <FormBase.InputContent
              filled={!!values?.firstName}
              inputRef="firstName"
              label="First Name"
              errorMessage={errors.firstName?.message}
              dataCy="label-firstName"
            >
              <FormBase.InputText
                dataCy="input-firstName"
                id="firstName"
                name="firstName"
                type="text"
                autoComplete="off"
                maxLength={30}
                disabled={loading}
              />
            </FormBase.InputContent>
            <FormBase.InputContent
              filled={!!values?.lastName}
              inputRef="lastName"
              label="Last Name"
              errorMessage={errors.lastName?.message}
              dataCy="label-lastName"
            >
              <FormBase.InputText
                dataCy="input-lastName"
                id="lastName"
                name="lastName"
                type="text"
                maxLength={30}
                disabled={loading}
              />
            </FormBase.InputContent>
          </FormBase.Grid>

          <FormBase.InputContent
            filled={!!values?.phone}
            inputRef="phone"
            label="Phone"
            errorMessage={errors.phone?.message}
            dataCy="label-phone"
          >
            <FormBase.InputText
              dataCy="input-phone"
              id="phone"
              name="phone"
              type="text"
              maxLength={15}
              disabled={loading}
            />
          </FormBase.InputContent>
        </FormBase.Content>
      </FormModal>
    </FormBase.Provider>
  );
};
